import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, FileSearch, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { submitLead, formatApiError } from "@/lib/api";

const KEY = "mb_statement_popup_seen";

export const StatementPopup = () => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return undefined;
    const trigger = () => {
      if (sessionStorage.getItem(KEY)) return;
      sessionStorage.setItem(KEY, "1");
      setShow(true);
    };
    const t = setTimeout(trigger, 15000);
    const onLeave = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitLead({ ...form, source: "statement_review" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="statement-popup"
          className="fixed inset-0 z-[60] flex items-end justify-center bg-[#050B1F]/60 p-3 backdrop-blur-sm sm:items-center sm:p-6" onClick={() => setShow(false)}>
          <motion.div onClick={(e) => e.stopPropagation()} data-lenis-prevent
            initial={{ y: 60, opacity: 0, rotate: -2 }} animate={{ y: 0, opacity: 1, rotate: 0 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 160, damping: 20 }}
            className="relative grid w-full max-w-3xl overflow-hidden rounded-[2rem] bg-white shadow-2xl md:grid-cols-5">
            <button onClick={() => setShow(false)} data-testid="statement-popup-close" aria-label="Close" className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-emerald-950 hover:bg-lime-300"><X className="h-4 w-4" /></button>
            <div className="relative overflow-hidden bg-lime-400 p-7 md:col-span-2">
              <div className="spin-slow absolute -bottom-20 -right-20 h-56 w-56 rounded-full border-[18px] border-dashed border-[#0B2A6F]/15" />
              <FileSearch className="h-10 w-10 text-[#0B2A6F]" />
              <p className="mt-6 font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-[#050B1F]">Free statement review.</p>
              <p className="mt-3 text-sm font-medium text-[#0B2A6F]">We'll show you exactly where your fees are going, and how to eliminate them.</p>
            </div>
            <div className="p-7 md:col-span-3">
              {done ? (
                <div className="flex h-full flex-col items-center justify-center py-8 text-center" data-testid="statement-popup-success">
                  <div className="grid h-14 w-14 place-items-center rounded-full bg-lime-400"><Check className="h-7 w-7" strokeWidth={3} /></div>
                  <p className="mt-4 font-display text-2xl font-bold">Request received</p>
                  <p className="mt-1 text-sm text-emerald-900/75">Your account manager will reach out to collect your statement.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">Before you go</p>
                  <p className="font-display text-xl font-bold leading-tight text-[#050B1F]">Are you paying fees you don't have to?</p>
                  {[["name", "Your name", "text", true], ["email", "Email", "email", true], ["phone", "Phone (optional)", "tel", false]].map(([k, p, t, r]) => (
                    <input key={k} data-testid={`statement-${k}-input`} type={t} required={r} placeholder={p} value={form[k]} onChange={set(k)}
                      className="w-full rounded-xl border border-emerald-900/15 px-4 py-3 text-[15px] outline-none focus:border-emerald-600 focus:ring-4 focus:ring-lime-300/40" />
                  ))}
                  <button disabled={busy} data-testid="statement-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2A6F] py-3.5 text-sm font-bold text-white hover:bg-[#050B1F] disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Review my statement free
                  </button>
                  <button type="button" onClick={() => setShow(false)} data-testid="statement-dismiss-btn" className="w-full text-xs text-emerald-900/70 hover:text-emerald-900">No thanks, I like paying fees</button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
